// Fail-closed client for the tenant feature-flag endpoint.
//
// Resolves the API base from `VITE_OROVIA_API_BASE` (same env var the preview
// plane uses in src/preview/config.ts / src/session/api.ts). When no base is
// configured — e.g. the standalone App with no backend — every flag reads OFF.
// The request is cookie-authed (`credentials: 'include'`); the endpoint is not
// yet built. This function NEVER throws: any network, HTTP, or parse failure
// resolves to the all-off snapshot so callers can render fail-closed.

import type { FlagsResponse } from './types'

/** Immutable all-off snapshot returned whenever flags cannot be resolved. */
export const ALL_OFF: FlagsResponse = Object.freeze({
  flags: Object.freeze({}) as Record<string, boolean>,
  evaluatedAt: '',
  ttlSeconds: 0,
})

/** Default flag route — the tenant-level preview plane read. */
export const VIEWER_FLAGS_PATH = '/v1/viewer/flags'
/** Per-principal flag route on the standalone auth plane (ENG-1166). */
export const AUTH_FLAGS_PATH = '/v1/auth/flags'

export interface FetchFlagsDeps {
  /** Override the resolved API base. Defaults to `VITE_OROVIA_API_BASE`. */
  apiBase?: string
  /**
   * Flag route to read. Defaults to the tenant preview route
   * ({@link VIEWER_FLAGS_PATH}); the standalone auth shell passes
   * {@link AUTH_FLAGS_PATH} so gating resolves for the real principal. Must be a
   * root-relative path beginning with `/`; any other value falls back to the
   * viewer route so a caller can never point the credentialed read off-origin.
   */
  path?: string
  /** Override the fetch implementation (for tests). Defaults to global fetch. */
  fetchImpl?: typeof fetch
}

/** Only same-origin, root-relative paths are honoured (fail-safe to viewer). */
function resolveFlagsPath(path?: string): string {
  return typeof path === 'string' && /^\/[A-Za-z0-9/_-]*$/.test(path)
    ? path
    : VIEWER_FLAGS_PATH
}

/** Read + trailing-slash-normalise the configured API base, or `null`. */
function resolveApiBase(explicit?: string): string | null {
  const base = (explicit ?? import.meta.env.VITE_OROVIA_API_BASE)?.replace(/\/$/, '')
  return base || null
}

/**
 * Coerce an untrusted JSON body into a `FlagsResponse`, fail-closed.
 * Only literal `true` counts as an enabled flag; anything else is `false`.
 */
function normalizeFlagsResponse(json: unknown): FlagsResponse {
  if (!json || typeof json !== 'object' || Array.isArray(json)) return ALL_OFF
  const body = json as Record<string, unknown>

  const flags: Record<string, boolean> = {}
  const rawFlags = body.flags
  if (rawFlags && typeof rawFlags === 'object' && !Array.isArray(rawFlags)) {
    for (const [key, value] of Object.entries(rawFlags)) {
      flags[key] = value === true
    }
  }

  const evaluatedAt = typeof body.evaluatedAt === 'string' ? body.evaluatedAt : ''
  const ttlSeconds =
    typeof body.ttlSeconds === 'number' && Number.isFinite(body.ttlSeconds) && body.ttlSeconds >= 0
      ? body.ttlSeconds
      : 0

  return { flags, evaluatedAt, ttlSeconds }
}

/**
 * Outcome of a single flags read. Every branch carries a fail-closed `response`
 * so a caller can always render safely, but the discriminant lets a caller tell
 * a *terminal* result apart from a *transient* one:
 *
 * - `ok`         — a real HTTP 200 with a parseable body. Terminal. The flags
 *                  are authoritative for this principal (an entitled user reads
 *                  their flags TRUE; an unentitled user legitimately reads them
 *                  all-off). Do not retry.
 * - `unconfigured` — no API base is set (e.g. the un-gated standalone/preview
 *                  app with no backend). Terminal all-off. Do not retry.
 * - `transient`  — a network error, a non-2xx status (401 pre-auth / 403 / 5xx),
 *                  or an unparseable 200 body. AMBIGUOUS: the caller may retry.
 *                  Notably a 401 fired before the post-login session cookie is
 *                  honoured lands here — it must NOT be cached as a permanent
 *                  "all flags off" (ENG-1166 fail-closed race).
 */
export type FlagsFetchOutcome =
  | { outcome: 'ok'; response: FlagsResponse }
  | { outcome: 'unconfigured'; response: FlagsResponse }
  | { outcome: 'transient'; response: FlagsResponse }

/**
 * Read the flag set once, reporting a discriminated {@link FlagsFetchOutcome} so
 * the provider can retry a transient failure instead of caching it fail-closed.
 * Never rejects.
 */
export async function fetchFlagsResult(deps: FetchFlagsDeps = {}): Promise<FlagsFetchOutcome> {
  const base = resolveApiBase(deps.apiBase)
  if (!base) return { outcome: 'unconfigured', response: ALL_OFF }

  const fetchImpl = deps.fetchImpl ?? fetch
  const path = resolveFlagsPath(deps.path)
  let response: Response
  try {
    response = await fetchImpl(`${base}${path}`, {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
      redirect: 'error',
      referrerPolicy: 'no-referrer',
    })
  } catch {
    // Network-shaped failure — ambiguous, retryable.
    return { outcome: 'transient', response: ALL_OFF }
  }

  // 401 (pre-auth / cookie not yet honoured), 403, 5xx: all ambiguous. Fail
  // closed for NOW, but let the caller retry — a genuinely-unentitled principal
  // is a 200 with flags off, not a non-2xx.
  if (!response.ok) return { outcome: 'transient', response: ALL_OFF }

  const json = await response.json().catch(() => null)
  // A 200 with an unreadable body is ambiguous (proxy/HTML interstitial); retry.
  if (json === null) return { outcome: 'transient', response: ALL_OFF }

  // A real 200 — authoritative, even when every flag is off (unentitled).
  return { outcome: 'ok', response: normalizeFlagsResponse(json) }
}

/**
 * Fetch the tenant flag set. Resolves to the all-off snapshot when no API base
 * is configured or on any failure. Never rejects.
 *
 * Thin fail-closed wrapper over {@link fetchFlagsResult}: it collapses the
 * outcome to just the snapshot. Callers that need to distinguish a transient
 * failure from a terminal read (to retry) should use {@link fetchFlagsResult}.
 */
export async function fetchFlags(deps: FetchFlagsDeps = {}): Promise<FlagsResponse> {
  const result = await fetchFlagsResult(deps)
  return result.response
}
